import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Grid, 
  CircularProgress, 
  Alert,
  Paper 
} from '@mui/material';
import DashboardCards from '../components/dashboard/DashboardCards';
import DashboardTable from '../components/dashboard/DashboardTable';
import FilterBar from '../components/dashboard/FilterBar';
import { 
  paymentsApi, 
  RealTimeStatusModel, 
  PaymentTrackingUISearchResultModel,
  SearchFilterType 
} from '../services/api/paymentsApi';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [dashboardStatus, setDashboardStatus] = useState<RealTimeStatusModel | null>(null);
  const [payments, setPayments] = useState<PaymentTrackingUISearchResultModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilterType>({
    paymentTrackingUISearchType: 'totalPaymentsProcessed',
    startSearchDateRange: new Date().toISOString().split('T')[0],
    endSearchDateRange: new Date().toISOString().split('T')[0],
    paymentType: 'outbound',
  });

  // Fetch dashboard status
  const fetchDashboardStatus = async () => {
    try {
      const data = await paymentsApi.getRealTimeStatus();
      setDashboardStatus(data);
    } catch (err) {
      console.error('Error fetching dashboard status:', err);
      setError('Failed to load dashboard status. Please try again later.');
    }
  };

  // Fetch payments based on filters
  const fetchPayments = async (searchFilters: SearchFilterType) => {
    setIsLoading(true);
    try {
      const data = await paymentsApi.searchPayments(searchFilters);
      setPayments(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError('Failed to load payments. Please try again later.');
      setPayments([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchDashboardStatus();
    fetchPayments(filters);
    
    // Refresh dashboard status every 30 seconds
    const intervalId = setInterval(() => {
      fetchDashboardStatus();
    }, 30000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Handle filter changes
  const handleApplyFilters = (newFilters: SearchFilterType) => {
    setFilters(newFilters);
    fetchPayments(newFilters);
  };

  // Handle dashboard card click
  const handleCardClick = (key: keyof RealTimeStatusModel) => {
    let searchType = 'totalPaymentsProcessed';
    
    // Map dashboard card keys to search types
    switch (key) {
      case 'psd2CriticalInTransit':
        searchType = 'psd2CriticalInTransit';
        break;
      case 'suspectedDuplicate':
        searchType = 'paymentSuspectedDuplicate';
        break;
      case 'awaitingRepair':
        searchType = 'paymentAwaitingRepair';
        break;
      case 'fraudCheckHeld':
        searchType = 'fraudCheckHeld';
        break;
      case 'sanctionsScreeningFailed':
        searchType = 'sanctionsScreeningFailed';
        break;
      case 'accountBalanceHoldFailed':
        searchType = 'accountBalanceHoldFailed';
        break;
      case 'liquidityCheckFailed':
        searchType = 'liquidityCheckFailed';
        break;
      default:
        searchType = 'totalPaymentsProcessed';
    }
    
    const newFilters = {
      ...filters,
      paymentTrackingUISearchType: searchType
    };
    
    setFilters(newFilters);
    fetchPayments(newFilters);
  };

  // Handle row click to navigate to payment details
  const handleRowClick = (payment: PaymentTrackingUISearchResultModel) => {
    navigate(`/payment/${payment.aggregateId}`);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Payment Operations Dashboard
      </Typography>
      
      {/* Dashboard Cards */}
      {dashboardStatus && (
        <DashboardCards 
          data={dashboardStatus} 
          onCardClick={handleCardClick}
        />
      )}
      
      {/* Filters */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Today's Payments</Typography>
        <FilterBar 
          onApplyFilters={handleApplyFilters}
          initialFilters={filters}
        />
      </Box>
      
      {/* Loading State */}
      {isLoading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center',
          my: 4,
          p: 3
        }}>
          <CircularProgress size={40} />
        </Box>
      )}
      
      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Payments Table */}
      {!isLoading && !error && payments.length > 0 && (
        <DashboardTable 
          data={payments}
          onRowClick={handleRowClick}
        />
      )}
      
      {/* Empty State */}
      {!isLoading && !error && payments.length === 0 && (
        <Paper elevation={0} sx={{ 
          p: 3, 
          textAlign: 'center',
          bgcolor: 'background.default',
          borderRadius: 1,
          mt: 2 
        }}>
          <Typography variant="body1" color="text.secondary">
            No payments found. Try adjusting your filters.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default DashboardPage; 